<script setup lang="ts">
  import { ref } from 'vue';

  import type { Invoice, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { MonetaryText } from '@ui/components/monetary';
  import { Timestamp } from '@ui/components/time';

  import InvoiceDrawer from '@/components/common/InvoiceDrawer.vue';
  import InvoicePromiseToPayPicker from '@/components/common/InvoicePromiseToPayPicker.vue';
  import InvoiceStatusStamp from '@/components/common/InvoiceStatusStamp.vue';
  import { PartnerQuickView } from '@/components/partners';
  import { useInvoiceDisplay } from '@/composables/useInvoiceDisplay';

  const props = withDefaults(
    defineProps<{
      invoice: Invoice;
      customer?: Partner;
      customerId?: string;
      customerName?: string;
      showCustomer?: boolean;
    }>(),
    { showCustomer: true },
  );

  const selectedInvoice = ref<Invoice>();
  const showSheet = ref(false);

  const { amountColorClass, hasPartialProgress } = useInvoiceDisplay(() => props.invoice);

  const handleSheetOpen = () => {
    selectedInvoice.value = props.invoice;
    showSheet.value = true;
  };

  const handleSheetClose = () => {
    selectedInvoice.value = undefined;
    showSheet.value = false;
  };
</script>

<template>
  <div class="border-border bg-card text-card-foreground w-full relative overflow-hidden border">
    <div class="flex items-center justify-between truncate gap-4 px-4 py-3">
      <div class="flex flex-col gap-x-2 z-2">
        <span class="uppercase text-3xs font-bold text-muted-foreground">Invoice</span>

        <Button
          v-if="invoice?.number"
          class="text-xs text-foreground font-bold truncate hover:underline cursor-pointer"
          variant="link"
          size="inline"
          @click="handleSheetOpen"
          >#{{ invoice?.number }}</Button
        >
      </div>

      <div class="flex items-center gap-x-2">
        <InvoiceStatusStamp
          :invoice="invoice"
          size="sm"
          badge-size="xs"
          :stamp-position="showCustomer ? '-right-1 -top-1' : 'right-0 -top-1'"
        />

        <template v-if="showCustomer">
          <PartnerQuickView
            v-if="customer || customerId"
            :partner="customer"
            :partner-id="customerId"
          />

          <strong v-else class="truncate">
            {{ customerName }}
          </strong>
        </template>
      </div>
    </div>

    <div class="border-border border-t-2 border-dotted" />

    <div class="divide-border flex items-stretch divide-x divide-dotted">
      <div class="flex flex-col flex-1 px-4 py-3 gap-y-1">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Amount
        </p>

        <p class="font-mono text-xs" :class="amountColorClass">
          <MonetaryText
            :value="invoice.pending_amount_remaining ?? invoice.amount_due"
            :currency="invoice.currency"
            smallest-unit
            :compact="false"
          />
          <span v-if="hasPartialProgress" class="text-muted-foreground ml-1 font-normal">
            of
            <MonetaryText
              :value="invoice.amount_due"
              :currency="invoice.currency"
              smallest-unit
              :compact="false"
            />
          </span>
        </p>
      </div>

      <div
        v-if="invoice.isPromise || invoice.isBrokenPromise"
        class="flex flex-col gap-y-1 shrink-0 px-4 py-3"
      >
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Promise
        </p>

        <InvoicePromiseToPayPicker :invoice="invoice">
          <Timestamp
            class="font-mono text-xs"
            :class="{
              'bg-destructive/20': invoice.isBrokenPromise,
              'bg-warning/20': invoice.isPromise && !invoice.isBrokenPromise,
            }"
            icon-class="text-2xs!"
            :time="invoice.promised_to_pay_date"
            format="L"
            fallback="Set date"
            date-only
            side="top"
            editable
            edit-icon-position="right"
          />
        </InvoicePromiseToPayPicker>
      </div>

      <div v-if="invoice.due_date" class="flex flex-col gap-y-1 shrink-0 px-4 py-3">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">Due</p>
        <Timestamp class="font-mono text-xs" :time="invoice.due_date" format="ll" date-only />
      </div>
    </div>

    <InvoiceDrawer
      v-if="selectedInvoice"
      v-model:open="showSheet"
      :invoice="selectedInvoice"
      :partner="customer"
      @close="handleSheetClose"
    />
  </div>
</template>
